import type {
    CycleAudienceCriteriaPayload,
    CycleAudiencePayload,
} from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import type { CycleAudienceTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { CycleEvaluationGroupAudienceCriteria } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { transformFiltersToCriteria } from '@/utils/cycleEvaluation';

export interface CycleAudienceSelection {
    audienceType: CycleAudienceTypeValue;
    filters: SelectedFilters;
    persistedCriterias: CycleEvaluationGroupAudienceCriteria[];
}

export const buildCycleAudiencePayload = ({
    audienceType,
    filters,
    persistedCriterias,
}: CycleAudienceSelection): CycleAudiencePayload => {
    if (audienceType === CycleAudienceType.ENTIRE_COMPANY) {
        return {
            cycleAudienceType: CycleAudienceType.ENTIRE_COMPANY,
            cycleAudienceCriterias: [],
        };
    }

    const criterias = transformFiltersToCriteria({
        filters,
        criterias: [],
        cycleAudienceId: '',
    });

    const idOfPersisted = new Map(
        persistedCriterias.map((criteria) => [
            `${criteria.criteriaType}:${criteria.value}`,
            criteria.id,
        ]),
    );

    return {
        cycleAudienceType: CycleAudienceType.FILTER_BY_ATTRIBUTES,
        cycleAudienceCriterias: criterias.map(
            ({ criteriaType, value, customAttributeId }): CycleAudienceCriteriaPayload => ({
                id: idOfPersisted.get(`${criteriaType}:${value}`) ?? null,
                criteriaType,
                customAttributeId: customAttributeId ?? null,
                value,
            }),
        ),
    };
};

export interface CycleAudienceSelectionIfHydrated extends CycleAudienceSelection {
    isAudienceHydrated: boolean;
}

// While the filters have not been restored, both audiences write an empty criteria list, erasing
// teams the administrator was never shown — the whole company included.
export const buildCycleAudiencePayloadIfHydrated = ({
    isAudienceHydrated,
    audienceType,
    filters,
    persistedCriterias,
}: CycleAudienceSelectionIfHydrated): CycleAudiencePayload | undefined =>
    isAudienceHydrated
        ? buildCycleAudiencePayload({ audienceType, filters, persistedCriterias })
        : undefined;
